import { useEffect, useRef, useState } from "react";
import ReactToPrint from "react-to-print";
import { QRCodeSVG } from "qrcode.react";
import axiosAdmin from "../../../../../api/apiAdmin";
import { errorToast } from "../../../toastAlerts/ToastAlerts";
import { apiError } from "../../../../../api/apiError";
import { BsX, BsPrinter } from "react-icons/bs";

/**
 * Printable parcel labels for OnWay orders — the sticker that goes on the box.
 *
 * OnWay's API has no label endpoint (their panel prints its own), so this
 * reproduces the same layout from what we actually sent them. The QR carries
 * the OnWay tracking number, which is what a courier scans.
 *
 * Sized for the SPRT SP-TL51 desktop label printer: 70x100mm roll stock, one
 * label per page. There is no A4 printer here, so there is no sheet layout —
 * a bulk run simply feeds N labels off the roll.
 */
/**
 * The stock the SP-TL51 can be loaded with, as its driver lists it.
 *
 * `perPage` is chosen so the BUSIEST label still fits its slot: eight parcel
 * codes, a long order_number and a cancelled stamp come to roughly 65mm. A
 * tighter fit would clip exactly the labels that carry the most information.
 * 108x50 is deliberately absent — nothing we print fits in 50mm.
 */
const STOCKS = [
  { key: "70x100", w: 70, h: 100, perPage: [1, 2, 3] },
  { key: "108x180", w: 108, h: 180, perPage: [2, 3] },
  { key: "108x220", w: 108, h: 220, perPage: [3, 4] },
];

/** What the printer cannot reach, per side. */
const MARGIN_MM = 2;

/**
 * How the label is drawn for a given slot height.
 *
 * Fitting three labels on 70x100 leaves 32mm each, and a full-size label needs
 * about 65mm — so the content has to give way, not just shrink. It gives way in
 * order of what someone actually needs off the box: the recipient, the tracking
 * number and the QR survive every tier; the sender line is the first to go,
 * since it is already recorded on the MGL Parcel line.
 *
 * Scaling everything by the same factor instead would put the address at 5px,
 * which is printed but not readable.
 */
const density = (slotMm) => {
  if (slotMm >= 62) {
    return { qr: 100, body: 10, recipient: 12, track: 14, ref: 11,
             showSender: true };
  }
  if (slotMm >= 42) {
    return { qr: 70, body: 8, recipient: 10, track: 12, ref: 9,
             showSender: true };
  }
  // 3-up on 70x100 leaves 32mm. The QR is the single biggest fixed cost, so it
  // takes the cut — 46px is ~12mm, still comfortably scannable on thermal.
  return { qr: 46, body: 7, recipient: 9, track: 11, ref: 8,
           showSender: false };
};
const OnWayLabels = ({ orderIds, filters, onClose }) => {
  const printRef = useRef();
  const [labels, setLabels] = useState(null);
  const [stockKey, setStockKey] = useState(STOCKS[0].key);
  const [perPage, setPerPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const stock = STOCKS.find((x) => x.key === stockKey) || STOCKS[0];
  // A count left over from another stock may not be offered here.
  const count = stock.perPage.includes(perPage) ? perPage : stock.perPage[0];
  const labelW = stock.w - MARGIN_MM * 2;
  // Every label gets the same height so the cuts fall on a regular grid — the
  // operator cuts a strip, not a jigsaw.
  const labelH = (stock.h - MARGIN_MM * 2) / count;
  const d = density(labelH);

  useEffect(() => {
    // Either an explicit id list, or the order-list filters — the second prints
    // everything the operator is looking at, not just the page they are on.
    const params = filters
      ? { all: 1, ...filters }
      : orderIds?.length
      ? { ids: orderIds.join(",") }
      : null;

    if (!params) return;

    setLoading(true);
    axiosAdmin
      .get(`/onway/labels`, { params })
      .then(({ data }) => setLabels(Array.isArray(data) ? data : []))
      .catch((err) => errorToast(apiError(err, "ეტიკეტი ვერ ჩაიტვირთა")))
      .finally(() => setLoading(false));
  }, [orderIds, filters]);

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[1100] flex items-center justify-center p-3"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg">
            ეტიკეტების ბეჭდვა
            {labels ? ` (${labels.length})` : ""}
            {labels?.length > 0 && (
              <span className="ml-2 text-xs font-normal text-slate-500">
                {labels.length} ეტიკეტი ·{" "}
                {Math.ceil(labels.length / count)} ფურცელი ({stock.w}×{stock.h}მმ,{" "}
                {labelH.toFixed(0)}მმ თითო)
              </span>
            )}
          </h2>
          <div className="flex items-center gap-3">
            {labels?.length > 0 && (
              <select
                className="h-9 px-2 border border-slate-300 rounded-md text-xs bg-white"
                value={stockKey}
                onChange={(e) => setStockKey(e.target.value)}
                title="ქაღალდის ზომა"
              >
                {STOCKS.map((x) => (
                  <option key={x.key} value={x.key}>
                    {x.w} × {x.h} მმ
                  </option>
                ))}
              </select>
            )}
            {labels?.length > 0 && (
              <select
                className="h-9 px-2 border border-slate-300 rounded-md text-xs bg-white"
                value={count}
                onChange={(e) => setPerPage(Number(e.target.value))}
                title="რამდენი ეტიკეტი ერთ ფურცელზე"
              >
                {stock.perPage.map((n) => (
                  <option key={n} value={n}>
                    {n} ცალი / ფურცელზე
                  </option>
                ))}
              </select>
            )}
            {labels?.length > 0 && (
              <ReactToPrint
                trigger={() => (
                  <button className="rounded-md bg-teal_600 text-white text-sm font-bold px-4 h-9 flex items-center gap-2">
                    <BsPrinter /> ბეჭდვა
                  </button>
                )}
                content={() => printRef.current}
                documentTitle="onway-labels"
              />
            )}
            <BsX
              className="text-gray-400 w-6 h-6 cursor-pointer"
              onClick={onClose}
            />
          </div>
        </div>

        {loading ? (
          <div className="animate-pulse h-64 bg-gray-100 rounded-lg" />
        ) : !labels?.length ? (
          <p className="text-sm text-slate-500 py-6 text-center">
            ეტიკეტი ვერ მოიძებნა
          </p>
        ) : (
          <div ref={printRef}>
            {/* Page break per label, and the browser's own header/footer off so
                the sticker isn't stamped with the page URL. */}
            <style>{`
              @media print {
                /* SPRT SP-TL51 roll stock. Several labels may share one page —
                   the operator cuts along the printed border — but a label is
                   never split across two: break-inside pushes one that will not
                   fit down to the next page instead. */
                @page { size: ${stock.w}mm ${stock.h}mm; margin: ${MARGIN_MM}mm; }
                html, body { width: ${stock.w}mm; }
                .onway-label {
                  break-inside: avoid;
                  page-break-inside: avoid;
                  margin: 0 !important;
                  width: ${labelW}mm !important;
                  height: ${labelH}mm;
                  overflow: hidden;
                }
                body { -webkit-print-color-adjust: exact; }
              }
            `}</style>

            {labels.map((l) => (
              <div
                key={l.id}
                className="onway-label border border-black px-2 py-1.5 mb-4 bg-white text-black
                           flex flex-col justify-start"
                style={{
                  width: `${labelW}mm`,
                  height: `${labelH}mm`,
                  fontSize: `${d.body}px`,
                  lineHeight: 1.2,
                  overflow: "hidden",
                }}
              >
                <div className="flex items-start justify-between border-b border-black pb-0.5 mb-1">
                  <span className="font-bold">
                    {l.id} - {l.date || ""}
                  </span>
                  <span className="font-bold tracking-wide" style={{ fontSize: `${d.recipient}px` }}>
                    ONWAY
                    {l.package_count > 1 && (
                      <span className="ml-1 font-normal">
                        {l.package_no}/{l.package_count}
                      </span>
                    )}
                  </span>
                </div>

                {d.showSender && (
                  <p className="border-b border-dashed border-black pb-0.5 mb-1">
                    <b>გამგზავნი:</b> {l.from_name}
                    {l.user_id != null && (
                      <span className="float-right font-bold">ID: {l.user_id}</span>
                    )}
                  </p>
                )}

                <p className="border-b border-dashed border-black pb-0.5 mb-1 leading-snug"
                  style={{ fontSize: `${d.recipient}px` }}>
                  <b>მიმღები:</b> {[l.to_name, l.to_address, l.to_city, l.to_phone]
                    .filter(Boolean)
                    .join(", ")}
                </p>

                <div className="flex items-center gap-2 border-b border-black pb-1 mb-1">
                  {l.tracking ? (
                    <QRCodeSVG value={String(l.tracking_base || l.tracking)} size={d.qr} level="M" />
                  ) : (
                    <div
                      className="border border-dashed border-black flex items-center justify-center text-[7px] text-center"
                      style={{ width: d.qr, height: d.qr }}
                    >
                      თრექინგი არ არის
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="border-b border-dashed border-black pb-0.5 mb-0.5">
                      რაოდ.: {l.quantity}
                    </p>
                    <p className="border-b border-dashed border-black pb-0.5 mb-0.5">
                      წონა: {l.weight}
                    </p>
                    {/* One long code must shrink rather than run off the
                        sticker — 15+ characters at 19px overflows 90mm. */}
                    <p
                      className="font-bold tracking-wide break-all leading-tight"
                      style={{
                        fontSize:
                          String(l.tracking || "").length > 18
                            ? `${d.track - 4}px`
                            : String(l.tracking || "").length > 13
                            ? `${d.track - 2}px`
                            : `${d.track}px`,
                      }}
                    >
                      # {l.tracking || "—"}
                    </p>
                  </div>
                </div>

                {/* Our own reference: the pack tracking numbers we sent as
                    order_number. This is what the warehouse matches the box
                    against, so it stays on the label whatever else is trimmed.
                    It used to be printed twice — a second, truncated copy sat
                    below under "ამანათები" — so only the full list remains, with
                    the count the other line carried. */}
                <p
                  className="font-bold break-all leading-tight"
                  style={{
                    fontSize:
                      String(l.order_number || "").length > 60
                        ? `${d.ref - 3}px`
                        : String(l.order_number || "").length > 32
                        ? `${d.ref - 2}px`
                        : `${d.ref}px`,
                  }}
                >
                  MGL Parcel: {l.order_number}
                  {l.packs?.length > 1 && (
                    <span className="font-normal"> ({l.packs.length} ამანათი)</span>
                  )}
                </p>

                {l.cancelled && (
                  <p className="mt-1 text-center font-bold border border-black py-0.5 text-[10px]">
                    გაუქმებული
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OnWayLabels;
